import {
  collapseObjects,
  fillObjectAllElements,
  fillObjectDefinedElements,
  fillObjectGapElements,
  objectEquals,
  projectObject,
  splitObjectByVariant,
} from "@/lib/blaze/physics";
import type {
  _Object,
  _Element,
  Coordinate,
  Variant,
} from "@/lib/blaze/reality";

export class Primitive {
  name!: string;
  operation!: (...args: any[]) => any;

  constructor(name: string, operation: (...args: any[]) => any) {
    this.name = name;
    this.operation = operation;
  }
}

export class Library {
  primitives: Primitive[] = [];

  constructor(primitives: Primitive[]) {
    this.primitives = primitives;
  }

  add(primitive: Primitive) {
    this.primitives.push(primitive);
  }

  get(name: string) {
    return this.primitives.find((p) => p.name === name);
  }

  list() {
    return this.primitives;
  }
}

const P = (name: string, operation: (...args: any[]) => any) =>
  new Primitive(name, operation);

export const PRIMITIVES = [
  // Essential primitives for turing completeness
  P("id", (a) => a),
  P("constant", (a) => () => a),
  P("true", () => true),
  P("false", () => false),
  P(
    "compose",
    (f, g) =>
      (...args: any[]) =>
        f(g(...args)),
  ),
  P("apply", (f, a) => f(a)),
  P("curry", (f) => (a: any) => (b: any) => f(a, b)),
  P("uncurry", (f) => (a: any) => (b: any) => f(a)(b)),

  // Arithmetic operations
  P("add", (a, b) => a + b),
  P("sub", (a, b) => a - b),
  P("mul", (a, b) => a * b),
  P("div", (a, b) => a / b),
  P("mod", (a, b) => a % b),
  P("pow", (a, b) => a ** b),
  P("neg", (a) => -a),

  // Comparison operations
  P("eq", (a, b) => a === b),
  P("neq", (a, b) => a !== b),
  P("gt", (a, b) => a > b),
  P("lt", (a, b) => a < b),
  P("gte", (a, b) => a >= b),
  P("lte", (a, b) => a <= b),

  // Logical operations
  P("and", (a, b) => a && b),
  P("or", (a, b) => a || b),
  P("not", (a) => !a),
  P("xor", (a, b) => a !== b),
  P("if", (a, b, c) => (a ? b : c)),

  // List operations
  P("concat", (a, b) => [...a, ...b]),
  P("length", (a) => a.length),
  P("head", (a) => a[0]),
  P("tail", (a) => a.slice(1)),
  P("last", (a) => a[a.length - 1]),
  P("map", (a, b) => a.map(b)),
  P("filter", (a, b) => a.filter(b)),
  P("reduce", (a, b, c) => a.reduce(b, c)),
  P("sort", (a) => a.sort()),
  P("reverse", (a) => a.reverse()),
  P("join", (a, b) => a.join(b)),
  P("pop", (a) => a.pop()),

  // Element operations
  P("elem_copy", (a) => a),
  P("elem_eqvariant", (el1, el2) => el1.variant === el2.variant),
  P("elem_eqa", (el1, el2) => el1.a === el2.a),
  P("elem_eqb", (el1, el2) => el1.b === el2.b),

  // Object operations
  P("obj_copy", (a) => a),
  P("obj_project", (a, b) => projectObject(a, b)),
  P("obj_collapse", (a) => collapseObjects(a)),
  P("obj_eq", (a, b) => objectEquals(a, b)),
  // P('obj_crop', (obj, [amin, amax, bmin, bmax]) => cropObject(obj, [amin, amax, bmin, bmax])),
  P("obj_split", (obj, v) => splitObjectByVariant(obj, v)),
  // P('obj_splitall', (obj) => splitObjectAllVariants(obj)),
  P("obj_filldefined", (obj, v: Variant) => fillObjectDefinedElements(obj, v)),
  P("obj_fillgaps", (obj, v: Variant) => fillObjectGapElements(obj, v)),
  P("obj_fillall", (obj, v: Variant) => fillObjectAllElements(obj, v)),
  // P('obj_overlap', (objects) => objectsOverlap(objects)),
  // P('obj_difference', (objects) => objectsDifference(objects)),
  // P('obj_rotate90', (obj) => rotateObject90(obj)),
  // P('obj_flip_h', (obj) => flipObjectHorizontally(obj)),
  // P('obj_flip_v', (obj) => flipObjectVertically(obj)),
];

export const LIBRARY = new Library(PRIMITIVES);
